"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Coins, LayoutDashboard, LogOut, Settings } from "lucide-react";
import { NotificationBell } from "./NotificationBell";
import type { SubscriptionTier } from "@/types";

const TIER_STYLES: Record<string, { label: string; className: string }> = {
  free: { label: "Free", className: "text-white/40 bg-white/[0.04] border-white/[0.06]" },
  starter: { label: "Starter", className: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  builder: { label: "Builder", className: "text-violet-400 bg-violet-500/10 border-violet-500/20" },
  launcher: { label: "Launcher", className: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
};

interface NavUserMenuProps {
  email: string;
  name?: string | null;
  credits: number;
  tier: SubscriptionTier | null;
}

export function NavUserMenu({ email, name: initialName, credits: initialCredits, tier: initialTier }: NavUserMenuProps) {
  const router = useRouter();
  const [credits, setCredits] = useState(initialCredits);
  const [currentTier, setCurrentTier] = useState<SubscriptionTier | null>(initialTier);
  const [displayName, setDisplayName] = useState(initialName || "");

  // Refresh credits + tier client-side on mount + every 30s
  useEffect(() => {
    const refresh = () => {
      fetch("/api/auth/me")
        .then((r) => r.ok ? r.json() : null)
        .then((data) => {
          if (data?.user) {
            if (data.user.totalCredits !== undefined) setCredits(data.user.totalCredits);
            if (data.user.subscriptionTier !== undefined) setCurrentTier(data.user.subscriptionTier);
            if (data.user.name) setDisplayName(data.user.name);
          }
        })
        // intentional: 30s nav-bar refresh; transient failure recovers on next tick or post-payment poll
        .catch(() => {});
    };

    refresh();
    const interval = setInterval(refresh, 30_000);

    // After payment: poll aggressively until credits change (webhook may take 1-5s)
    const onPayment = () => {
      let attempts = 0;
      const poll = setInterval(() => {
        attempts++;
        refresh();
        if (attempts >= 10) clearInterval(poll); // stop after ~10s
      }, 1000);
    };
    window.addEventListener("credits-updated", onPayment);
    return () => { clearInterval(interval); window.removeEventListener("credits-updated", onPayment); };
  }, []);

  const handleLogout = async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    router.push("/");
    router.refresh();
  };

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1.5 rounded-lg border border-subtle bg-white/[0.04] px-2.5 py-1.5 text-sm">
        <Coins className="size-3.5 text-amber-400" />
        <span className="font-medium text-light">{credits}</span>
      </div>

      <NotificationBell />

      {(() => {
        const style = TIER_STYLES[currentTier ?? "free"];
        return (
          <Link
            href="/settings"
            className={`hidden sm:inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-opacity hover:opacity-80 ${style.className}`}
          >
            {style.label}
          </Link>
        );
      })()}

      <button
        onClick={() => router.push("/dashboard")}
        className="flex items-center gap-1.5 text-sm text-white/50 hover:text-white/80 transition-colors"
      >
        <LayoutDashboard className="size-3.5" />
        <span className="hidden sm:inline">{displayName || email}</span>
      </button>

      <Link
        href="/settings"
        className="text-white/50 hover:text-white/80 transition-colors"
        title="Settings"
      >
        <Settings className="size-3.5" />
      </Link>

      <button
        onClick={handleLogout}
        className="text-white/50 hover:text-white/80 transition-colors"
        title="Sign out"
      >
        <LogOut className="size-3.5" />
      </button>
    </div>
  );
}
